import { api } from '@/lib/axios';

// ── Enums ──────────────────────────────────────────────────────────────────

export type AiProvider = 'ANTHROPIC' | 'OPENAI';

// ── Response-Typen ─────────────────────────────────────────────────────────

/** Spiegelt TenantAiConfigView (Backend). Cipher wird nie exponiert. */
export interface TenantAiConfig {
  id: string;
  tenantId: string;
  provider: AiProvider;
  model: string | null;
  apiKeySuffix: string | null;
  hasApiKey: boolean;
  monthlyLimit: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  /** AP-43: je Aufgabe der gebuchten Bausteine das Modell, das arbeitet, und woher es kommt. */
  taskModels?: AiTaskModel[];
}

export interface AiTaskModel {
  task: string;
  label: string;
  model: string;
  /** TENANT = eigene Wahl; TASK, OPERATOR und CODE = Vorgabe von Symphenia. */
  source: 'TENANT' | 'TASK' | 'OPERATOR' | 'CODE';
}

export interface AiProviderInfo {
  provider: AiProvider;
  supportedModels: string[];
  defaultModel: string;
  capabilities: {
    text: boolean;
    vision: boolean;
    embeddings: boolean;
    streaming: boolean;
  };
}

export interface AiUsage {
  period: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  requests: number;
  limit: number;
  percentageUsed: number;
  byok: boolean;
}

export interface AiTestResult {
  provider: AiProvider;
  model: string;
  centrallyBilled: boolean;
  content: string;
  usage: { inputTokens: number; outputTokens: number };
}

// ── Request-Typen ──────────────────────────────────────────────────────────

export interface UpdateAiConfigPayload {
  provider?: AiProvider;
  /** `null` = „Vorgabe von Symphenia" (AP-43). */
  model?: string | null;
  apiKey?: string;
  enabled?: boolean;
}

export interface AiTestPayload {
  prompt?: string;
  model?: string;
  maxTokens?: number;
  temperature?: number;
}

// ── API ────────────────────────────────────────────────────────────────────

export const aiApi = {
  /** Tenant-AI-Konfiguration laden (ohne Cipher). */
  getConfig: () =>
    api.get<TenantAiConfig>('/ai/config'),

  /** Provider, Modell, Limit oder BYOK-Key aktualisieren. */
  updateConfig: (payload: UpdateAiConfigPayload) =>
    api.patch<TenantAiConfig>('/ai/config', payload),

  /** Hinterlegten BYOK-Key löschen (Fallback auf zentralen Key). */
  deleteApiKey: () =>
    api.delete<TenantAiConfig>('/ai/config/api-key'),

  /** Liste aller verfügbaren Provider mit Modellen + Capabilities. */
  getProviders: () =>
    api.get<AiProviderInfo[]>('/ai/providers'),

  /** Aktuelle Monatsnutzung + Limit-Info. */
  getUsage: () =>
    api.get<AiUsage>('/ai/usage/current'),

  /** Diagnose-Call: Testet Key + Modell mit einem einfachen Prompt. */
  test: (payload?: AiTestPayload) =>
    api.post<AiTestResult>('/ai/test', payload ?? {}),
};
